import { useState, useCallback } from 'react';

interface StreamMessage {
  type: 'token' | 'actions' | 'done' | 'error';
  content?: string; // For 'token' type
  actions?: any[]; // For 'actions' type
  conversation_id?: string; // For 'done' type
  message?: string; // For 'error' type
}

interface UseChatStreamOptions {
  onToken: (token: string) => void;
  onActions: (actions: any[]) => void;
  onComplete: (conversationId: string) => void;
  onError: (error: string) => void;
}

export function useChatStream({ onToken, onActions, onComplete, onError }: UseChatStreamOptions) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const sendMessage = useCallback(
    async (payload: {
      message: string;
      tenant_id: string;
      user_id: string;
      user_name: string;
      conversation_id?: string;
      fileMetadata?: any;
    }) => {
      // Create abort controller for cancellation
      const controller = new AbortController();
      setAbortController(controller);
      setIsStreaming(true);

      try {
        const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
        const response = await fetch(`${apiUrl}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'include',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response body is null');
        }

        // Read SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          // Decode chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE messages (separated by \n\n)
          const messages = buffer.split('\n\n');
          buffer = messages.pop() || ''; // Keep incomplete message in buffer

          for (const message of messages) {
            if (!message.trim() || !message.startsWith('data: ')) {
              continue;
            }

            try {
              // Parse SSE message
              const jsonString = message.replace(/^data: /, '');
              const data = JSON.parse(jsonString) as StreamMessage;

              // Handle different event types
              switch (data.type) {
                case 'token':
                  if (data.content) {
                    onToken(data.content);
                  }
                  break;

                case 'actions':
                  if (data.actions) {
                    onActions(data.actions);
                  }
                  break;

                case 'done':
                  if (data.conversation_id) {
                    onComplete(data.conversation_id);
                  }
                  break;

                case 'error':
                  onError(data.message || 'Unknown error');
                  break;
              }
            } catch (parseError) {
              console.error('Failed to parse SSE message:', message, parseError);
            }
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('Stream aborted by user');
        } else {
          onError(error instanceof Error ? error.message : 'Stream failed');
        }
      } finally {
        setIsStreaming(false);
        setAbortController(null);
      }
    },
    [onToken, onActions, onComplete, onError]
  );

  const cancelStream = useCallback(() => {
    if (abortController) {
      abortController.abort();
    }
  }, [abortController]);

  return {
    sendMessage,
    isStreaming,
    cancelStream,
  };
}
